import { FC, ReactNode, useContext, useEffect } from "react";
import type { PageProps } from "gatsby";
import {
  MIXPANEL_CUSTOM_PAGE_VIEW,
  MIXPANEL_PATH_PAGE_NAME,
  MixpanelProperties,
} from "./mixpanelTypes";
import { MixpanelContext } from "src/utils/mixpanel/MixpanelContext";

/*
    This Wrapper has 2 purposes:

    1. Fire a Mixpanel Page View event for any page view in MWEB: https://hingehealth.atlassian.net/browse/MWEB-1674

      Some groups of pre-existing pages have their own unique ways of handling page views, so they are excluded from this.

      If your page has custom page view logic (ex. Contentful DEI pages), add it to the
      MIXPANEL_CUSTOM_PAGE_VIEW list in mixpanelTypes.ts and fire a page view event in a useEffect hook.

    2. Have a global way to setup Link tracking in MWEB: https://hingehealth.atlassian.net/browse/MWEB-1801

      For a link to be tracked, you need to add the className "mixpanel-link" to the link you want to be tracked.

      In order for Mixpanel users to identify which link this is in the dashboard, you should add the
      componentName prop to your link. 

      For links that are hidden on initial page load, you may have to call registerLinkClick again in that component.
      Refer to "FeaturedSectionWithBackground.tsx" 

      **IMPORTANT**
     When calling registerLinkClick for hidden components, you need to use a different class other than "mixpanel-link" to
     select those links. If "mixpanel-link" is used in these subsequent calls to registerLinkClick, duplicate Mixpanel events
     may be reported.

      Example: <Link
          href={withPrefixSafe(featuredItem.link)}
          variant="primary"
          dark={false}
          className="mixpanel-link"
          title="appropriate title for this link"
        > 
*/

export const hasCustomPageView = (path: string) => {
  // The homepage needs its own page-view logic too (home_page_test's
  // Variant must be registered before it fires, same reasoning as MWEB-2391's
  // fix for hingehealth-website's homepage) -- but "/" has no first subpath
  // segment for the firstSubpath check below to match, so it needs its own
  // explicit case.
  if (path === "/") {
    return true;
  }

  let isCustomPage = false;

  const firstSubpath = `/${path.split("/")[1]}/`;

  if (MIXPANEL_CUSTOM_PAGE_VIEW.includes(firstSubpath)) {
    isCustomPage = true;
  }

  return isCustomPage;
};

/*
  Returns a page name for the page.
  If the page handle's it's own page view event, empty string is returned.
  If the page has a pre-defined page name that is returned instead.
*/
export const getPageNameFromPath = (path: string) => {
  const currentPath: string = path;

  if (hasCustomPageView(currentPath)) {
    return "";
  }

  if (currentPath in MIXPANEL_PATH_PAGE_NAME) {
    return MIXPANEL_PATH_PAGE_NAME[currentPath];
  }

  return currentPath
    .split(/[-/]/)
    .map(i => i.charAt(0).toUpperCase() + i.substring(1))
    .join(" ")
    .trim();
};

export const ApplyMixpanelHook: FC<ApplyMixpanelHookProps> = ({
  props,
  children,
}) => {
  const { trackPageView, registerLinkClick } = useContext(MixpanelContext);

  useEffect(() => {
    // Add tracking to all pages, track all links with .mixpanel-link class.
    const path = props.path;
    if (trackPageView && getPageNameFromPath(path) !== "") {
      trackPageView(getPageNameFromPath(path));
    }

    // Track all links with the .mixpanel-link className.
    if (registerLinkClick) {
      registerLinkClick(
        ".mixpanel-link",
        "Link Clicked",
        (ele: HTMLElement): MixpanelProperties => {
          return { "Component Clicked": ele.title ? ele.title : "undefined" };
        },
      );
    }
  }, [trackPageView, registerLinkClick, props]);

  return <>{children}</>;
};

export interface ApplyMixpanelHookProps {
  props: PageProps;
  children: ReactNode;
}
